import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const DashboardContainer = styled.div`
  min-height: 100vh;
  background: #0a0a0a;
  padding-top: 100px;
`;

const DashboardHeader = styled.div`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  padding: 60px 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    width: 300px;
    height: 300px;
    background: radial-gradient(
      circle,
      rgba(255, 215, 0, 0.1) 0%,
      transparent 70%
    );
    top: -150px;
    right: 100px;
  }
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 1;
`;

const WelcomeText = styled(motion.h1)`
  font-family: "Playfair Display", serif;
  font-size: 3rem;
  color: #ffffff;
  margin-bottom: 1rem;
`;

const UserStats = styled.div`
  display: flex;
  gap: 3rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const StatCard = styled(motion.div)`
  background: rgba(255, 215, 0, 0.1);
  padding: 1.5rem 2.5rem;
  border-radius: 15px;
  border: 1px solid rgba(255, 215, 0, 0.2);
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #ffd700;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  color: #b0b0b0;
  font-size: 1rem;
`;

const ContentSection = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 60px 20px;
`;

const SectionTitle = styled.h2`
  font-family: "Playfair Display", serif;
  font-size: 2.5rem;
  color: #ffffff;
  margin-bottom: 3rem;

  span {
    color: #ffd700;
  }
`;

const LearningPath = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 30px;
  margin-bottom: 60px;
`;

const PathCard = styled(motion.div)`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  padding: 2rem;
  border-radius: 20px;
  border: 1px solid rgba(255, 215, 0, 0.2);
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-5px);
    border-color: rgba(255, 215, 0, 0.5);
    box-shadow: 0 20px 40px rgba(255, 215, 0, 0.1);
  }

  &::before {
    content: "";
    position: absolute;
    top: -50%;
    right: -50%;
    width: 100%;
    height: 100%;
    background: radial-gradient(
      circle,
      rgba(255, 215, 0, 0.1) 0%,
      transparent 70%
    );
    transition: all 0.5s ease;
  }

  &:hover::before {
    top: -25%;
    right: -25%;
  }
`;

const PathIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const PathTitle = styled.h3`
  font-size: 1.5rem;
  color: #ffd700;
  margin-bottom: 1rem;
`;

const PathDescription = styled.p`
  color: #b0b0b0;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const PathProgress = styled.div`
  background: rgba(255, 255, 255, 0.1);
  height: 8px;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.5rem;
`;

const ProgressBar = styled.div`
  background: linear-gradient(90deg, #ffd700 0%, #ffa500 100%);
  height: 100%;
  width: ${(props) => props.progress}%;
  transition: width 0.5s ease;
`;

const ProgressText = styled.div`
  color: #b0b0b0;
  font-size: 0.9rem;
  text-align: right;
`;

const UpcomingLessons = styled.div`
  display: grid;
  gap: 20px;
`;

const LessonCard = styled(motion.div)`
  background: #1a1a2e;
  padding: 2rem;
  border-radius: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid rgba(255, 215, 0, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(255, 215, 0, 0.3);
    transform: translateX(10px);
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const LessonInfo = styled.div``;

const LessonTitle = styled.h4`
  color: #ffffff;
  font-size: 1.3rem;
  margin-bottom: 0.5rem;
`;

const LessonMeta = styled.div`
  color: #b0b0b0;
  font-size: 1rem;
  display: flex;
  gap: 2rem;
`;

const StartButton = styled(motion.button)`
  padding: 0.8rem 2rem;
  background: linear-gradient(135deg, #ffd700 0%, #ffa500 100%);
  color: #0a0a0a;
  border: none;
  border-radius: 50px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 10px 30px rgba(255, 215, 0, 0.3);
  }
`;
const Dashboard = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const language = i18n.language;

  const learningPaths = [
    {
      id: 1,
      icon: "🎯",
      title: "Business English",
      description: "Master professional communication for global business",
      progress: 65,
      totalLessons: 24,
      completedLessons: 16,
    },
    {
      id: 2,
      icon: "🗣️",
      title: "Conversational Fluency",
      description: "Speak naturally with native-like pronunciation",
      progress: 40,
      totalLessons: 30,
      completedLessons: 12,
    },
    {
      id: 3,
      icon: "✈️",
      title: "Travel Essentials",
      description: "Essential phrases for luxury travel experiences",
      progress: 85,
      totalLessons: 20,
      completedLessons: 17,
    },
  ];

  const upcomingLessons = [
    {
      id: 1,
      title: "Negotiation Strategies",
      duration: "45 min",
      type: "Live Session",
      time: "Today, 3:00 PM",
    },
    {
      id: 2,
      title: "Accent Reduction Workshop",
      duration: "60 min",
      type: "Group Class",
      time: "Tomorrow, 2:00 PM",
    },
    {
      id: 3,
      title: "Business Email Writing",
      duration: "30 min",
      type: "Self-paced",
      time: "Available Now",
    },
  ];

  return (
    <DashboardContainer>
      <DashboardHeader>
        <HeaderContent>
          <WelcomeText
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Welcome back, Reza
          </WelcomeText>
          <UserStats>
            <StatCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <StatNumber>10,000+</StatNumber>
              <StatLabel>{t("hero.stats.students", language)}</StatLabel>
              <StatLabel>Current Level</StatLabel>
            </StatCard>
            <StatCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <StatNumber>10,000+</StatNumber>
              <StatLabel>{t("hero.stats.students", language)}</StatLabel>
              <StatLabel>Lessons Completed</StatLabel>
            </StatCard>
            <StatCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <StatNumber>10,000+</StatNumber>
              <StatLabel>{t("hero.stats.students", language)}</StatLabel>
              <StatLabel>Day Streak</StatLabel>
            </StatCard>
          </UserStats>
        </HeaderContent>
      </DashboardHeader>

      <ContentSection>
        <SectionTitle>
          Your <span>Learning Paths</span>
        </SectionTitle>
        <LearningPath>
          {learningPaths.map((path, index) => (
            <PathCard
              key={path.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
              onClick={() => navigate(`/lesson/${path.id}`)}
            >
              <PathIcon>{path.icon}</PathIcon>
              <PathTitle>{path.title}</PathTitle>
              <PathDescription>{path.description}</PathDescription>
              <PathProgress>
                <ProgressBar progress={path.progress} />
              </PathProgress>
              <ProgressText>
                {path.completedLessons}/{path.totalLessons} lessons •{" "}
                {path.progress}%
              </ProgressText>
            </PathCard>
          ))}
        </LearningPath>

        <SectionTitle>
          Upcoming <span>Lessons</span>
        </SectionTitle>
        <UpcomingLessons>
          {upcomingLessons.map((lesson, index) => (
            <LessonCard
              key={lesson.id}
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              onClick={() => navigate(`/lesson/${lesson.id}`)}
            >
              <LessonInfo>
                <LessonTitle>{lesson.title}</LessonTitle>
                <LessonMeta>
                  <span>⏱ {lesson.duration}</span>
                  <span>📚 {lesson.type}</span>
                  <span>📅 {lesson.time}</span>
                </LessonMeta>
              </LessonInfo>
              <StartButton
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Start Lesson
              </StartButton>
            </LessonCard>
          ))}
        </UpcomingLessons>
      </ContentSection>
    </DashboardContainer>
  );
};

export default Dashboard;
